import React from 'react';
import { Sun, Zap, Shield, Sparkles, MapPin, TrendingUp, Leaf } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const Paraguay = () => {
  const navigate = useNavigate();
  
  const handleNavigate = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const reasons = [
    {
      icon: Sun,
      title: '300+ días de sol',
      description: 'Uno de los índices más altos de Sudamérica.',
    },
    {
      icon: Zap,
      title: 'Independencia total',
      description: 'Olvidate de los cortes de luz y de los aumentos de tarifa.',
    },
    {
      icon: Shield,
      title: 'Respaldo legal',
      description: 'La Ley 6977 te permite vender tu energía sobrante a la ANDE.',
    },
    {
      icon: Sparkles,
      title: 'Evitá los cortes de luz',
      description: 'Tu hogar o negocio siempre con energía, sin interrupciones.',
    },
    {
      icon: TrendingUp,
      title: 'Retorno garantizado',
      description: 'Recuperá tu inversión en 5 a 7 años y después es ganancia pura.',
    },
    {
      icon: Leaf,
      title: 'Energía 100% limpia',
      description: 'Reducí tu huella de carbono y cuidá el medio ambiente.',
    },
  ];

  // Comparativa de radiación solar por país (kWh/m²/día promedio anual)
  const solarComparison = [
    { country: 'Paraguay', flag: '🇵🇾', radiation: 5.5, highlight: true },
    { country: 'España', flag: '🇪🇸', radiation: 4.8, highlight: false },
    { country: 'Italia', flag: '🇮🇹', radiation: 4.3, highlight: false },
    { country: 'Alemania', flag: '🇩🇪', radiation: 2.9, highlight: false },
  ];

  return (
    <section id="paraguay" className="pt-0 pb-12 sm:pb-16 md:pb-20 bg-gradient-to-b from-zinc-900 to-zinc-950" data-testid="paraguay-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12">
        {/* Separador visual */}
        <div className="w-full h-px bg-zinc-800 mb-10 sm:mb-14 md:mb-16"></div>
        
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-medium uppercase tracking-widest text-emerald-400 mb-4">
            Paraguay & Energía Solar
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            El país perfecto para<br />
            <span className="text-emerald-400">vivir del sol</span>
          </h2>
          <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed px-2">
            Paraguay tiene todo para que tu hogar o negocio funcione con energía solar. 
            <span className="text-white font-medium"> No es el futuro, es el presente.</span>
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start mb-16">
          
          {/* Left: Reasons - 6 cards in 2 columns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {reasons.map((reason, index) => {
              const Icon = reason.icon;
              return (
                <div 
                  key={index} 
                  className="bg-zinc-800/50 border border-zinc-700/50 rounded-2xl p-5 hover:border-emerald-500/30 transition-all duration-300"
                  data-testid={`reason-${index}`}
                >
                  <div className="bg-emerald-500/10 w-11 h-11 rounded-xl flex items-center justify-center mb-3">
                    <Icon className="text-emerald-400" size={22} />
                  </div>
                  <h3 className="text-base font-semibold text-white mb-1.5">{reason.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{reason.description}</p>
                </div>
              );
            })}
          </div>

          {/* Right: Message + Solar Comparison */}
          <div className="space-y-8">
            {/* Emotional Message */}
            <div className="bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 rounded-3xl p-8">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-6 leading-snug">
                No se trata solo de ahorrar plata.
              </h3>
              <div className="space-y-4 text-zinc-300">
                <p>
                  Se trata de <span className="text-emerald-400 font-semibold">vivir tranquilo</span>. 
                  De saber que tu casa o negocio tiene energía propia, limpia, silenciosa.
                </p>
                <p>
                  De no preocuparte por los aumentos de la ANDE. 
                  De ser parte de la <span className="text-emerald-400 font-semibold">innovación</span> que está transformando Paraguay.
                </p>
                <p className="text-white font-medium pt-2">
                  El sol está ahí todos los días. ¿Por qué no aprovecharlo?
                </p>
              </div>
            </div>

            {/* Solar Comparison Card */}
            <div className="bg-zinc-800/70 border border-zinc-700/50 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="text-emerald-400" size={20} />
                <h4 className="text-lg font-semibold text-white">¿Por qué Paraguay?</h4>
              </div>
              <p className="text-sm text-zinc-400 mb-5">
                Alemania es líder mundial en energía solar, pero tiene <span className="text-white">casi la mitad de radiación</span> que Paraguay. 
                Imaginate el potencial que tenemos.
              </p>
              
              {/* Comparison bars */}
              <div className="space-y-3">
                {solarComparison.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <span className="text-xl w-8">{item.flag}</span>
                    <span className={`w-20 text-sm ${item.highlight ? 'text-emerald-400 font-semibold' : 'text-zinc-400'}`}>
                      {item.country}
                    </span>
                    <div className="flex-1 h-6 bg-zinc-900/50 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full flex items-center justify-end pr-3 transition-all duration-500 ${
                          item.highlight 
                            ? 'bg-gradient-to-r from-emerald-600 to-emerald-400' 
                            : 'bg-zinc-600'
                        }`}
                        style={{ width: `${(item.radiation / 5.5) * 100}%` }}
                      >
                        <span className={`text-xs font-bold ${item.highlight ? 'text-zinc-900' : 'text-zinc-300'}`}>
                          {item.radiation} kWh/m²
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-zinc-500 mt-4 text-center">
                Radiación solar promedio diaria (kWh/m²/día) - Fuente: Global Solar Atlas
              </p>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
          <button
            onClick={() => handleNavigate('/calculadora')}
            className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold px-8 py-4 rounded-xl transition-all duration-300"
            data-testid="paraguay-calculator-cta"
          >
            <Zap size={20} />
            Calculá tu ahorro
          </button>
          <button
            onClick={() => handleNavigate('/servicios/residenciales')}
            className="inline-flex items-center justify-center gap-2 border border-zinc-600 hover:border-emerald-500/50 text-white px-8 py-4 rounded-xl transition-all duration-300"
          >
            Conocé más sobre nuestros servicios
          </button>
        </div>

        {/* Bottom Stats - Simple */}
        <div className="border-t border-zinc-800 pt-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl md:text-4xl font-bold text-emerald-400">5.5</p>
              <p className="text-sm text-zinc-500 mt-1">kWh/m² de radiación diaria</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-emerald-400">85%</p>
              <p className="text-sm text-zinc-500 mt-1">Ahorro en tu factura</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-emerald-400">5-7</p>
              <p className="text-sm text-zinc-500 mt-1">Años de retorno</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-emerald-400">25</p>
              <p className="text-sm text-zinc-500 mt-1">Años de garantía</p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Paraguay;
